/**
 * Adapter contracts (Contract v1). The UI and use cases depend only on these interfaces, so
 * swapping IndexedDB for Supabase/Dexie or the mocks for real WhatsApp/gateway needs no UI change.
 */
import type { BookingDraft, BookResult, MoveRequest, MoveResult } from '../domain/booking';
import type { ListFilter, Tx } from '../domain/tx';
import type {
  ConsentPurpose, E164, EntityMap, EntityName, Id, IsoUtc, Message, Payment, SuppressionReason,
} from '../domain/types';
import type { TemplateCall } from './messaging/templates';

export type { ListFilter, Tx } from '../domain/tx';
export type Unsubscribe = () => void;

// ------------------------------------------------------------------ storage

/** Emitted after a change commits. `remote` = made by another tab. Re-read rows; events carry no data. */
export interface ChangeEvent {
  entity: EntityName;
  type: 'put' | 'delete' | 'reset';
  id?: Id;
  remote: boolean;
}

export interface Repo<E extends EntityName> {
  get(id: Id): Promise<EntityMap[E] | undefined>;
  list(filter?: ListFilter<EntityMap[E]>): Promise<EntityMap[E][]>;
  /** Insert or replace. Callers stamp updatedAt. */
  put(row: EntityMap[E]): Promise<EntityMap[E]>;
  delete(id: Id): Promise<void>;
  subscribe(cb: (e: ChangeEvent) => void): Unsubscribe;
}

export type Tables = { [E in EntityName]: EntityMap[E][] };
export interface Dump { version: 1; exportedAt: IsoUtc; data: Tables }
/** Any subset of tables; missing tables are empty. */
export type SeedData = Partial<Tables>;

export interface StorageAdapter {
  readonly kind: 'indexeddb' | 'memory' | 'supabase';
  repo<E extends EntityName>(entity: E): Repo<E>;
  /**
   * Runs `fn` atomically over `entities`. Reads and writes are isolated from other tabs and rolled
   * back if `fn` throws. Inside `fn` only await `tx.*` calls (IndexedDB closes the transaction otherwise).
   */
  transact<R>(entities: readonly EntityName[], fn: (tx: Tx) => Promise<R>): Promise<R>;
  /** Atomic availability re-check + insert: of two simultaneous bookings for a slot exactly one wins. */
  bookAppointment(draft: BookingDraft, now: IsoUtc): Promise<BookResult>;
  /** Atomic availability re-check + move. */
  moveAppointment(req: MoveRequest, now: IsoUtc): Promise<MoveResult>;
  /** Subscribe to one entity or '*' for any. Fires for this tab's and other tabs' commits. */
  subscribe(entity: EntityName | '*', cb: (e: ChangeEvent) => void): Unsubscribe;
  exportAll(): Promise<Dump>;
  /** Replaces all data with the dump. */
  importAll(dump: Dump): Promise<void>;
  /** Wipes everything and loads the seed (or nothing). */
  reset(seed?: SeedData): Promise<void>;
  close(): void;
}

/** Stores a booking transaction touches. */
export const BOOKING_STORES: readonly EntityName[] = ['businesses', 'settings', 'staff', 'services', 'weeklyHours', 'timeOff', 'appointments', 'clients'];

// ------------------------------------------------------------------ messaging

export type MessageRecord = Message;
export interface SendRequest {
  businessId: Id;
  to: E164;
  call: TemplateCall;
  clientId: Id;
  appointmentId?: Id;
  purpose: ConsentPurpose;
  /** One live message per key; a repeat returns the existing record. */
  dedupeKey: string;
  scheduledFor?: IsoUtc;
  /** Set when a quiet-hours deferral moved the send time. */
  deferred?: boolean;
}
export interface OutboxFilter { businessId?: Id; status?: Message['status']; appointmentId?: Id; clientId?: Id }

export interface MessagingAdapter {
  readonly label: string;
  /** Enqueue a message (status queued). The scheduler has already checked consent, plan and quiet hours. */
  send(req: SendRequest): Promise<MessageRecord>;
  /** Record a message that was NOT sent, with the reason (consent, free plan, allowance). */
  suppress(req: SendRequest, reason: SuppressionReason): Promise<MessageRecord>;
  /** Cancels a queued (or manual-queue) message. */
  cancel(id: Id): Promise<void>;
  /** Moves a queued message to a new time (quiet-hours deferral). */
  reschedule(id: Id, scheduledFor: IsoUtc, reason?: SuppressionReason): Promise<void>;
  /** Turns a queued message into a suppressed one (consent revoked, plan changed). */
  markSuppressed(id: Id, reason: SuppressionReason): Promise<void>;
  /** Marks a manual-queue message as sent by the owner via wa.me. */
  markManualSent(id: Id): Promise<void>;
  listOutbox(filter?: OutboxFilter): Promise<MessageRecord[]>;
  onStatus(cb: (m: MessageRecord) => void): Unsubscribe;
  /** Advances statuses queued -> sent -> delivered according to the clock. Returns messages changed. */
  tick(): Promise<number>;
  /** Optional: marks a queued, already-due message as sent right now (interactive templates). Returns the updated row. */
  dispatchNow?(id: Id): Promise<MessageRecord | undefined>;
}

// ------------------------------------------------------------------ payments

export interface DepositRequest { businessId: Id; appointmentId: Id; amountAgorot: number; holdMinutes: number }
export interface DepositCreated { paymentId: Id; checkoutUrl: string }
export type CheckoutOutcome = 'success' | 'failure' | 'timeout';
export type RefundResult = { ok: true; payment: Payment } | { ok: false; reason: 'not_found' | 'not_paid' | 'exceeds_paid' };

export interface PaymentsAdapter {
  readonly label: string;
  createDeposit(req: DepositRequest): Promise<DepositCreated>;
  getStatus(paymentId: Id): Promise<Payment | undefined>;
  refund(paymentId: Id, amountAgorot?: number): Promise<RefundResult>;
  /** Mock checkout buttons: Success / Fail / Timeout. */
  complete(paymentId: Id, outcome: CheckoutOutcome): Promise<Payment | undefined>;
  /** Expires pending payments whose hold ran out (clock driven). Returns the expired payments. */
  sweepExpired(): Promise<Payment[]>;
  onStatus(cb: (p: Payment) => void): Unsubscribe;
}

// ------------------------------------------------------------------ auth

export interface Session {
  token: string;
  phone: E164;
  createdAt: IsoUtc;
  expiresAt: IsoUtc;
  ownerId?: Id;
  businessId?: Id;
}
export type OtpRequestResult = { ok: true; phone: E164 } | { ok: false; reason: 'invalid_phone' | 'rate_limited' };
export type OtpVerifyResult =
  | { ok: true; session: Session }
  | { ok: false; reason: 'invalid_phone' | 'no_code' | 'wrong_code' | 'expired' | 'too_many_attempts' };

export interface AuthAdapter {
  readonly label: string;
  requestOtp(phone: string): Promise<OtpRequestResult>;
  /** `persist: false` verifies a code without creating a session (customer "find my booking"). */
  verifyOtp(phone: string, code: string, opts?: { persist?: boolean }): Promise<OtpVerifyResult>;
  getSession(): Session | undefined;
  /** Attach the owner's business to the current session. */
  linkSession(ownerId: Id, businessId: Id): void;
  signOut(): void;
  /** Demo only: the code the "SMS" would contain. */
  getDemoCode(phone: string): string | undefined;
  subscribe(cb: (s: Session | undefined) => void): Unsubscribe;
}

// ------------------------------------------------------------------ clock

export interface Clock {
  /** Epoch milliseconds (demo clock = real time + offset). */
  now(): number;
  nowIso(): IsoUtc;
  advance(ms: number): void;
  setNow(ms: number): void;
  reset(): void;
  /** Current offset from real time, persisted across reloads. */
  offsetMs(): number;
  /** Real time at which the demo clock was last anchored to its fixed start (only when anchoring is on). */
  anchoredAt?(): number | undefined;
  subscribe(cb: (nowMs: number) => void): Unsubscribe;
}

// ------------------------------------------------------------------ key-value (sessions, clock offset, role)

export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}
